import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useSearchParams } from 'react-router-dom';

import { mockSignInPath, requestMagicLink, USE_MOCK } from '../api/client';
import { LangToggle, useT } from '../i18n';
import { describeApiError } from '../lib/apiErrors';

interface FormValues {
  email: string;
}

export function LoginPage(): JSX.Element {
  const t = useT();
  const { register, handleSubmit, formState } = useForm<FormValues>({
    defaultValues: { email: '' },
  });
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [params] = useSearchParams();
  const sessionExpired = params.get('session') === 'expired';

  const onSubmit = async (values: FormValues): Promise<void> => {
    setError(null);
    try {
      await requestMagicLink(values.email);
      setSent(true);
    } catch (e) {
      setError(describeApiError(e, t));
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-16">
      {/* The Layout header (and its toggle) does not wrap the auth pages —
          render the same language switch here so it is reachable pre-login. */}
      <div className="flex justify-end mb-4">
        <LangToggle />
      </div>
      <h1 className="text-2xl font-bold text-slate-900">{t('login.title')}</h1>
      <p className="mt-2 text-slate-600">{t('login.intro')}</p>
      {sessionExpired && !sent ? (
        <div
          role="status"
          className="mt-6 rounded-md bg-amber-50 ring-1 ring-amber-200 p-3 text-sm text-amber-900"
        >
          {t('login.sessionExpired')}
        </div>
      ) : null}

      {sent ? (
        <div className="card p-6 mt-8">
          <h2 className="font-semibold text-slate-900">{t('login.sentTitle')}</h2>
          {/* The mock backend sends no e-mail — its card below says so. */}
          {!USE_MOCK ? <p className="mt-2 text-sm text-slate-600">{t('login.sentBody')}</p> : null}
          {USE_MOCK ? (
            // No e-mail leaves a mock backend — hand over the link directly.
            <div className="mt-4 rounded-md bg-amber-50 ring-1 ring-amber-200 p-3 text-xs text-amber-900 space-y-2">
              <p>
                <strong>{t('login.demoModeTitle')}</strong> {t('login.demoModeBody')}
              </p>
              {mockSignInPath() !== null ? (
                <Link to={mockSignInPath()!} className="btn-primary py-1.5 text-xs">
                  {t('login.demoModeSignIn')}
                </Link>
              ) : null}
            </div>
          ) : null}
          <button className="btn-secondary mt-4 text-xs" onClick={() => setSent(false)}>
            {t('login.otherEmail')}
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="card p-6 mt-8 space-y-4">
          <div>
            <label htmlFor="email" className="label">
              {t('login.emailLabel')}
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              autoFocus
              {...register('email', { required: true })}
              className="input mt-1"
              placeholder={t('login.emailPlaceholder')}
            />
          </div>
          {error ? <div className="text-sm text-red-600">{error}</div> : null}
          <button type="submit" disabled={formState.isSubmitting} className="btn-primary w-full">
            {formState.isSubmitting ? t('login.submitting') : t('login.submit')}
          </button>
          <p className="text-xs text-slate-500 text-center">
            {t('login.termsPrefix')}{' '}
            <Link to="/terms" className="text-brand-700 hover:underline">
              {t('login.termsLink')}
            </Link>
            .
          </p>
        </form>
      )}
    </div>
  );
}
